import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Flex,
  Mentions,
  Segmented,
  Tag,
  Typography,
  Upload,
  theme,
} from 'antd';
import {
  ArrowUpOutlined,
  InboxOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { useAppSelector } from '@/hooks/reduxHooks';
import { MAX_MESSAGE_LENGTH } from '../chatSession';
import { isLenderRecord } from '../recordRef';
import { useAgentChat } from '../useAgentChat';
import {
  agentLabel,
  chatSuggestions,
  DRAFT_MESSAGE,
  offStateText,
} from '../display';
import {
  isTagSearch,
  TAG_AGENTS,
  TAG_DESCRIPTIONS,
  tagMatches,
  tagsFor,
  withTag,
  type Tag as AgentTag,
} from '../tags';
import { useSkillEdits } from '../SkillEditsProvider';
import TurnView from './TurnView';
import AgentsPanel from './AgentsPanel';

const MAX_SHEET_BYTES = 15 * 1024 * 1024;
const TOO_LARGE = 'This file is over 15 MB.';
const LENDER_EMPTY_TEXT =
  "Drop this lender's rate sheet below to update its pricing, or ask a general question.";

// The composer's `@` list (OMT-1776): each agent's name over its one-line description.
const tagOptions = (tags: readonly AgentTag[]) =>
  tags.map((tag) => ({
    value: tag,
    label: (
      <Flex vertical>
        <Typography.Text strong>{agentLabel(TAG_AGENTS[tag])}</Typography.Text>
        <Typography.Text type="secondary">
          {TAG_DESCRIPTIONS[tag]}
        </Typography.Text>
      </Flex>
    ),
  }));
const TAG_POPUP = 'kali-tag-options';

/** The highlighted row of the `@` list, or null when it is closed (rc-mentions unmounts the popup then). */
const activeTagOption = (): HTMLElement | null =>
  document.querySelector<HTMLElement>(
    `.${TAG_POPUP} .ant-mentions-dropdown-menu-item-active`,
  );

const AgentChatPanel = () => {
  const {
    state,
    busy,
    send,
    importSheet,
    act,
    rate,
    newChat,
    recordRef,
    mode,
    client,
  } = useAgentChat();
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [view, setView] = useState<'Chat' | 'Agents'>('Chat');
  // Unsaved skill-editor text lives in SkillEditsProvider (above the drawer and
  // the Agents page), so it survives the Chat/Agents switch and is shared.
  const { edits: skillEdits, onEdits: setSkillEdits } = useSkillEdits();
  const { token } = theme.useToken();
  // The tags this user may use: `@ca-lead` is offered to admins and CA leads only (OMT-1846).
  const userData = useAppSelector((s) => s.auth.userData);
  const tags = useMemo(() => tagsFor(userData), [userData]);
  const options = useMemo(() => tagOptions(tags), [tags]);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [state.turns]);

  if (mode === 'off') {
    const offState = offStateText(import.meta.env.DEV);
    return (
      <Alert
        style={{ margin: 16 }}
        type="info"
        showIcon
        message={offState.message}
        description={offState.hint}
      />
    );
  }

  const submit = (text: string) => {
    if (busy || !text.trim()) return;
    void send(text);
    setDraft('');
  };

  return (
    <Flex vertical style={{ height: '100%' }}>
      <Flex
        align="center"
        justify="space-between"
        gap={8}
        style={{ padding: '0 16px 8px' }}
      >
        <Typography.Text type="secondary">
          {isLenderRecord(recordRef)
            ? 'Lender settings are on screen'
            : recordRef
              ? `Assessment ${recordRef.id.slice(0, 8)}… is on screen`
              : 'No assessment on screen: general questions only'}
        </Typography.Text>
        <Flex align="center" gap={8}>
          <Segmented
            size="small"
            options={['Chat', 'Agents']}
            value={view}
            onChange={(v) => setView(v as 'Chat' | 'Agents')}
          />
          {mode === 'mock' && <Tag color="orange">Mock stream</Tag>}
          {view === 'Chat' && (
            <Button size="small" icon={<PlusOutlined />} onClick={newChat}>
              New chat
            </Button>
          )}
        </Flex>
      </Flex>

      {view === 'Agents' ? (
        <AgentsPanel
          client={client}
          edits={skillEdits}
          onEdits={setSkillEdits}
        />
      ) : (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px' }}>
            {state.turns.length === 0 && (
              <Flex vertical gap={8} style={{ paddingTop: 16 }}>
                <Typography.Text type="secondary">
                  {isLenderRecord(recordRef)
                    ? LENDER_EMPTY_TEXT
                    : 'Ask about the assessment on screen, or a general question.'}{' '}
                  Answers cite their sources.
                </Typography.Text>
                {chatSuggestions(recordRef).map((suggestion) => (
                  <Button
                    key={suggestion}
                    onClick={() => submit(suggestion)}
                    style={{
                      height: 'auto',
                      whiteSpace: 'normal',
                      textAlign: 'left',
                    }}
                  >
                    {suggestion}
                  </Button>
                ))}
              </Flex>
            )}
            {state.turns.map((turn) => (
              <TurnView
                key={turn.id}
                turn={turn}
                onRate={(rating) => rate(turn, rating)}
                onAct={(card, action, reason, selected) =>
                  void act(turn, card, action, reason, selected)
                }
                busy={busy}
                // Not submit(): that clears the composer, and a typed draft stays.
                onRunAgain={() => {
                  if (!busy) void send(DRAFT_MESSAGE);
                }}
              />
            ))}
            <div ref={endRef} />
          </div>

          <Flex
            vertical
            gap={8}
            style={{
              padding: 16,
              borderTop: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            {isLenderRecord(recordRef) && (
              <>
                <Upload.Dragger
                  showUploadList={false}
                  multiple={false}
                  accept=".pdf,.png,.jpg,.jpeg"
                  disabled={busy}
                  beforeUpload={(file) => {
                    if (file.size > MAX_SHEET_BYTES) {
                      setSheetError(TOO_LARGE);
                    } else {
                      setSheetError(null);
                      void importSheet(file);
                    }
                    return false;
                  }}
                >
                  <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                  </p>
                  <p className="ant-upload-hint">
                    Drop a lender's rate sheet (PDF or screenshot)
                  </p>
                </Upload.Dragger>
                {sheetError && (
                  <Typography.Text type="danger">{sheetError}</Typography.Text>
                )}
              </>
            )}
            <Flex gap={8} wrap>
              {tags.map((tag) => (
                <Button
                  key={tag}
                  size="small"
                  onClick={() => setDraft((d) => withTag(d, tag))}
                >
                  {`@${agentLabel(TAG_AGENTS[tag])}`}
                </Button>
              ))}
            </Flex>
            <Flex gap={8}>
              <Mentions
                value={draft}
                onChange={setDraft}
                options={options}
                filterOption={(search, option) =>
                  tagMatches(option.value ?? '', search)
                }
                // Offered only at the start: the server routes on a leading tag.
                validateSearch={(search) => isTagSearch(draft, search, tags)}
                placement="top"
                popupClassName={TAG_POPUP}
                onKeyDown={(e) => {
                  // rc-mentions picks the highlighted tag on Enter but not on Tab.
                  const active = activeTagOption();
                  if (!active) return;
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault();
                    active.click();
                  } else if (e.key === 'Escape') {
                    // rc-mentions closes the list; keep the Drawer open.
                    e.stopPropagation();
                  }
                }}
                // rc-mentions skips this while the `@` list is open, so Enter picks a tag there.
                onPressEnter={(e) => {
                  if (e.shiftKey) return;
                  e.preventDefault();
                  submit(draft);
                }}
                maxLength={MAX_MESSAGE_LENGTH}
                autoSize={{ minRows: 1, maxRows: 6 }}
                placeholder="Ask KALI AI…"
              />
              <Button
                type="primary"
                aria-label="Send"
                icon={<ArrowUpOutlined />}
                disabled={busy || !draft.trim()}
                onClick={() => submit(draft)}
              />
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  );
};

export default AgentChatPanel;
